// SoundManager.ts

import { AudioGenerator, GeneratedSoundType, getAudioGenerator } from './AudioGenerator';
import { AudioPlayer, MP3SoundType } from './AudioPlayer'; // <<< New Import

// ===============================================
// Configuration: Select which sounds are MP3 vs Generated
// ===============================================

/**
 * Generated sound keys (those that will still be handled by AudioGenerator)
 * This is useful for UI sounds that don't need high fidelity (e.g., button clicks).
 */
const GENERATED_SOUNDS_TO_KEEP: GeneratedSoundType[] = [
    // 'button_click', 
    // 'ui_alert',
];

/**
 * Combined list of all available sound effects.
 * We'll use the MP3 sounds for all chess actions for high quality.
 */
export type SoundType = MP3SoundType | 'generated_sound_fallback'; // Extend with MP3 types

// ===============================================
// Sound Manager Class (Updated)
// ===============================================

export class SoundManager {
    private static instance: SoundManager | null = null;
    
    // Core Web Audio context (shared between generator and player)
    private audioContext: AudioContext;

    private audioGenerator: AudioGenerator;
    private audioPlayer: AudioPlayer; // <<< New instance for MP3s

    private isSoundEnabled: boolean = true;
    private volume: number = 0.5;

    /**
     * Private constructor for singleton pattern
     */
    private constructor() {
        // Initialize or get the shared AudioContext
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

        // Pass the context to the generator (assuming AudioGenerator.ts can handle this)
        this.audioGenerator = getAudioGenerator(); 

        // Initialize the new MP3 player
        this.audioPlayer = new AudioPlayer(this.audioContext);
    }

    /**
     * Get the singleton instance
     * @returns The SoundManager instance
     */
    static getInstance(): SoundManager {
        if (!SoundManager.instance) {
            SoundManager.instance = new SoundManager();
        }
        return SoundManager.instance;
    }

    /**
     * Load all sounds (NOW DECODES MP3 FILES)
     * @returns Promise that resolves when all MP3 files are decoded.
     */
    async loadSounds(): Promise<void> {
        // Load MP3s and wait for them to be decoded
        await this.audioPlayer.loadMP3Sounds();
        console.log('All sounds loaded and ready for playback.');
    }

    /**
     * Play a sound effect
     * @param sound The sound to play (now includes MP3 types)
     */
    play(sound: SoundType): void {
        if (!this.isSoundEnabled) return;
        
        // 1. Check if the sound is one of the loaded MP3s
        if (sound === 'gameStart' || sound === 'capture' || sound === 'castle' || sound === 'promotion' || sound === 'check' || sound === 'gameEnd') {
            this.audioPlayer.play(sound, this.volume);
        // 2. Otherwise, delegate to the AudioGenerator (for generated sounds)
        } else {
            // NOTE: You'll need to adapt AudioGenerator.ts to handle the AudioContext and volume
            // For now, we'll just call the generator's play method.
            this.audioGenerator.play(sound as GeneratedSoundType); 
        }
    }

    /**
     * Play the appropriate sound for a move (USES MP3s)
     */
    playMoveSound(
        isCapture: boolean = false,
        isCastle: boolean = false,
        isPromotion: boolean = false,
        isCheck: boolean = false
    ): void {
        if (!this.isSoundEnabled) return;

        let soundToPlay: MP3SoundType;

        if (isCapture) {
            soundToPlay = 'capture';
        } else if (isCastle) {
            soundToPlay = 'castle';
        } else if (isPromotion) {
            soundToPlay = 'promotion';
        } else if (isCheck) {
            soundToPlay = 'check';
        } else {
            soundToPlay = 'move';
        }

        this.audioPlayer.play(soundToPlay, this.volume);
    }

    // --- Volume / State Management ---

    /**
     * Enable or disable sounds
     */
    setEnabled(enabled: boolean): void {
        this.isSoundEnabled = enabled;
        this.audioGenerator.setEnabled(enabled); // Keep generator in sync
    }

    isEnabled(): boolean {
        return this.isSoundEnabled;
    }

    toggle(): boolean {
        this.isSoundEnabled = !this.isSoundEnabled;
        this.audioGenerator.setEnabled(this.isSoundEnabled);
        return this.isSoundEnabled;
    }

    setVolume(volume: number): void {
        this.volume = Math.min(1.0, Math.max(0.0, volume));
        this.audioGenerator.setVolume(this.volume); // Keep generator in sync
    }

    getVolume(): number {
        return this.volume;
    }

    /**
     * Unlock audio after user interaction
     */
    async unlockAudio(): Promise<void> {
        if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
            console.log('Audio context resumed.');
        }
        await this.audioGenerator.unlockAudio(); // Keep generator logic for safety
    }

    /**
     * Clean up resources
     */
    destroy(): void {
        this.audioGenerator.destroy();
        // Since AudioContext is shared, destroying it should be done carefully,
        // but for a full cleanup, closing it is correct.
        this.audioContext.close(); 
        SoundManager.instance = null;
    }
}

/**
 * Get the global SoundManager instance
 * Convenience function for accessing the singleton
 */
export function getSoundManager(): SoundManager {
    return SoundManager.getInstance();
}